'use client';

import { useCallback, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import * as tus from 'tus-js-client';

import { MediaWriteService, type StorageBucket } from '@kit/shared/storage';

export type UploadStatus =
  | 'idle'
  | 'uploading'
  | 'paused'
  | 'completed'
  | 'failed';

export interface ChunkedUploadOptions {
  bucket: StorageBucket;
  folder: string;
  chunkSize?: number;
  onProgress?: (progress: number) => void;
  onComplete?: (result: UploadResult) => void;
  onError?: (error: Error) => void;
}

export interface UploadResult {
  storagePath: string;
  bucket: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
}

export interface ChunkedUploadResult {
  upload: (file: File) => Promise<UploadResult | null>;
  pause: () => void;
  resume: () => void;
  cancel: () => void;
  progress: number;
  status: UploadStatus;
  error: Error | null;
}

const DEFAULT_CHUNK_SIZE = 6 * 1024 * 1024; // 6MB - Supabase default
const LARGE_FILE_THRESHOLD = 6 * 1024 * 1024; // Use TUS for files > 6MB

/**
 * Hook for handling large file uploads with chunking and resumable support
 *
 * Uses standard upload for small files and TUS protocol for large files.
 *
 * @param client - Supabase client instance
 * @param options - Upload configuration options
 * @returns Upload controls and status
 *
 * @example
 * ```tsx
 * function UploadComponent() {
 *   const client = useSupabaseClient();
 *   const { upload, pause, resume, cancel, progress, status } = useChunkedUpload(client, {
 *     bucket: 'venue-documents',
 *     folder: 'venue-123/photos',
 *     onProgress: (p) => console.log(`${p}% complete`),
 *     onComplete: (result) => console.log('Upload complete:', result),
 *     onError: (err) => console.error('Upload failed:', err),
 *   });
 *
 *   return (
 *     <div>
 *       <button onClick={() => upload(file)}>Upload</button>
 *       {status === 'uploading' && (
 *         <>
 *           <progress value={progress} max={100} />
 *           <button onClick={pause}>Pause</button>
 *         </>
 *       )}
 *       {status === 'paused' && <button onClick={resume}>Resume</button>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useChunkedUpload(
  client: SupabaseClient,
  options: ChunkedUploadOptions,
): ChunkedUploadResult {
  const {
    bucket,
    folder,
    chunkSize = DEFAULT_CHUNK_SIZE,
    onProgress,
    onComplete,
    onError,
  } = options;

  const [status, setStatus] = useState<UploadStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<Error | null>(null);

  const tusUploadRef = useRef<tus.Upload | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const generateStoragePath = useCallback(
    (file: File): string => {
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 8);
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      return `${folder}/${timestamp}-${randomStr}-${sanitizedName}`;
    },
    [folder],
  );

  const uploadStandard = useCallback(
    async (file: File, storagePath: string): Promise<UploadResult | null> => {
      abortControllerRef.current = new AbortController();

      const uploadResult = await new MediaWriteService(client).uploadObject(
        bucket,
        storagePath,
        file,
        {
          cacheControl: '3600',
          upsert: false,
        },
      );

      if (uploadResult.isFailure) {
        throw new Error(uploadResult.getError().message);
      }

      return {
        storagePath,
        bucket,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
      };
    },
    [bucket, client],
  );

  const uploadTus = useCallback(
    async (file: File, storagePath: string): Promise<UploadResult | null> => {
      // Get the Supabase project URL and access token
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error(
          'NEXT_PUBLIC_SUPABASE_URL is not configured. Check your environment variables.',
        );
      }

      const { data: sessionData } = await client.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      if (!accessToken) {
        throw new Error('Authentication required for upload');
      }

      return new Promise((resolve, reject) => {
        const upload = new tus.Upload(file, {
          endpoint: `${supabaseUrl}/storage/v1/upload/resumable`,
          retryDelays: [0, 1000, 3000, 5000],
          chunkSize,
          headers: {
            authorization: `Bearer ${accessToken}`,
            'x-upsert': 'false',
          },
          uploadDataDuringCreation: true,
          removeFingerprintOnSuccess: true,
          metadata: {
            bucketName: bucket,
            objectName: storagePath,
            contentType: file.type,
            cacheControl: '3600',
          },
          onError: (err) => {
            setStatus('failed');
            setError(err);
            onError?.(err);
            reject(err);
          },
          onProgress: (bytesUploaded, bytesTotal) => {
            const percentage = Math.round((bytesUploaded / bytesTotal) * 100);
            setProgress(percentage);
            onProgress?.(percentage);
          },
          onSuccess: () => {
            setStatus('completed');
            setProgress(100);
            const result: UploadResult = {
              storagePath,
              bucket,
              fileName: file.name,
              fileSize: file.size,
              mimeType: file.type,
            };
            onComplete?.(result);
            resolve(result);
          },
        });

        tusUploadRef.current = upload;

        // Check for previous uploads
        upload.findPreviousUploads().then((previousUploads) => {
          if (previousUploads.length > 0 && previousUploads[0]) {
            upload.resumeFromPreviousUpload(previousUploads[0]);
          }
          upload.start();
        });
      });
    },
    [bucket, chunkSize, client.auth, onComplete, onError, onProgress],
  );

  const upload = useCallback(
    async (file: File): Promise<UploadResult | null> => {
      setStatus('uploading');
      setProgress(0);
      setError(null);

      const storagePath = generateStoragePath(file);

      try {
        let result: UploadResult | null;

        if (file.size > LARGE_FILE_THRESHOLD) {
          // Use TUS for large files
          result = await uploadTus(file, storagePath);
        } else {
          // Use standard upload for small files
          // Simulate progress for small files (they upload quickly)
          setProgress(50);
          result = await uploadStandard(file, storagePath);
          setProgress(100);
          setStatus('completed');
          if (result) {
            onComplete?.(result);
          }
        }

        return result;
      } catch (err) {
        const uploadError =
          err instanceof Error ? err : new Error('Upload failed');
        setStatus('failed');
        setError(uploadError);
        onError?.(uploadError);
        return null;
      }
    },
    [generateStoragePath, onComplete, onError, uploadStandard, uploadTus],
  );

  const pause = useCallback(() => {
    if (tusUploadRef.current && status === 'uploading') {
      tusUploadRef.current.abort();
      setStatus('paused');
    }
  }, [status]);

  const resume = useCallback(() => {
    if (tusUploadRef.current && status === 'paused') {
      setStatus('uploading');
      tusUploadRef.current.start();
    }
  }, [status]);

  const cancel = useCallback(() => {
    if (tusUploadRef.current) {
      tusUploadRef.current.abort();
      tusUploadRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setStatus('idle');
    setProgress(0);
    setError(null);
  }, []);

  return {
    upload,
    pause,
    resume,
    cancel,
    progress,
    status,
    error,
  };
}
